// Port of filter-app's computeFilterLifeDisplay (lib/services/filter_status/
// filter_life_display.dart, FS-432) so the analysis summary shows exactly what
// the homeowner's app shows and the two can never contradict each other.
//
// Both inputs the app uses are honoured: the device's airflow floor AND the
// alert-derived replacement status, which the app treats as authoritative over
// the floor (an open filter_needs_replacement alert says "Change now"/"Change
// soon" even while the floor still reads healthy). The console reads those
// alert docs through /fb/devices/enrich (firebase-api) — the same Firestore
// documents, scoped to the same current filter, that the app's filter card
// streams.
//
// The legacy filters/{fid}.status field is deliberately not consulted — the app
// writes 'calibrating' at create time and the cloud never advances it, so it
// stays stuck (see filter_life_display.dart).

export type FilterLifeDisplay =
  | { kind: 'expect_reading' }
  | { kind: 'change_now' }
  | { kind: 'change_by'; date: string }
  | { kind: 'change_soon' }
  | { kind: 'life_pct'; percent: number };

/// Mirrors the app's FilterStatus enum for filter replacement.
export type FilterReplacementStatus = 'normal' | 'warning' | 'urgent';

/// Replacement threshold (percent of life remaining) by filter thickness.
/// 1" filters clog faster relative to their depth, so they alert earlier.
export const filterReplacementThreshold = (thickness: number): number =>
  thickness <= 1.0 ? 10 : 5;

/// Port of the app's AlertStatusAdapter.replacementStatus. Keyed on the exact
/// alert type, never on a category mapping: the app maps *unknown* alert types
/// into the filter-replacement category as a surfacing fallback, so routing
/// replacement state through categories would let an unrelated urgent alert
/// trigger the Change-now UI (FS-432).
export const replacementStatusFromAlerts = (
  alerts: ReadonlyArray<{ type?: string | null; severity?: string | null }> | null | undefined,
): FilterReplacementStatus => {
  let max: FilterReplacementStatus = 'normal';
  for (const alert of alerts ?? []) {
    if (alert?.type !== 'filter_needs_replacement') continue;
    if (alert.severity === 'urgent') return 'urgent';
    if (alert.severity === 'warning') max = 'warning';
  }
  return max;
};

export const computeFilterLifeDisplay = (
  airflowPercentageFloor: number | null | undefined,
  thickness: number | null | undefined,
  predictedChangeDate?: string | null,
  replacementStatus: FilterReplacementStatus = 'normal',
): FilterLifeDisplay => {
  const floor = airflowPercentageFloor;

  // Floor-null is the only genuine "no reading yet" signal.
  if (floor == null) return { kind: 'expect_reading' };

  // Change now: an urgent replacement alert, or life fully exhausted.
  if (replacementStatus === 'urgent' || floor <= 0) return { kind: 'change_now' };

  // Below threshold: a warning replacement alert, or the local fallback for the
  // window before an alert arrives. Show the date when known.
  const atOrBelowThreshold = floor <= filterReplacementThreshold(thickness ?? 1.0);
  if (replacementStatus === 'warning' || atOrBelowThreshold) {
    return predictedChangeDate
      ? { kind: 'change_by', date: predictedChangeDate }
      : { kind: 'change_soon' };
  }

  // Healthy — show the floor percentage. Clamp to 100; the cloud floor can
  // briefly exceed 100 from sensor noise or a still-settling baseline.
  return { kind: 'life_pct', percent: Math.min(100, Math.round(floor)) };
};
